import type { MenuItem, MenuItemSeparator } from 'ui/menu/menu';

import type { WorkOrderProgressEvent } from 'cadenza/api-client/dto/workorder-dto';
import type { WorkOrderId } from 'cadenza/api-client/workorder-api';
import { observeForResult } from 'cadenza/api-client/workorder-api';
import type { EnrichmentResultEvent } from 'cadenza/geocoding/geocoding-progress-dialog/geocoding-progress-dialog';
import { getStore } from 'cadenza/store/cadenza-store';
import { compareWithLocale } from 'cadenza/utils/i18n/i18n';
import type { ActiveAttributeId } from 'cadenza/workbook/api/active-attribute-id';
import type { EnrichmentType } from 'cadenza/workbook/api/enrichment-type';
import { compareEnrichmentType } from 'cadenza/workbook/api/enrichment-type';
import type { Objecttype } from 'cadenza/workbook/api/objecttype';
import type { WorkbookAttributeDto } from 'cadenza/workbook/api/workbook-attribute-dto';
import { createEnrichment } from 'cadenza/workbook/enrichment/create/create-enrichment';
import type { EnrichmentCandidateDto } from 'cadenza/workbook/enrichment/model/enrichment-candidate-dto';
import type { AttributeId } from 'cadenza/workbook/model/attribute-id';
import i18n from 'cadenza/workbook/workbook-panel/databrowser/create-enrichment-context-menu-utilities.properties';
import { enrichmentActionsAvailable } from 'cadenza/workbook/workbook-panel/databrowser/enrichment-details-context-menu-utilities';
import { getEnrichmentByAttributeId, getObjecttypeById } from 'cadenza/workbook/workbook.state';

interface CreateEnrichObjectTypeActionsOptions {
  enrichmentCandidates: EnrichmentCandidateDto[];
  objecttype: Objecttype;
}

interface CreateEnrichAttributeActionsOptions extends CreateEnrichObjectTypeActionsOptions {
  attribute: WorkbookAttributeDto;
}

export async function createEnrichObjecttypeActions({
  enrichmentCandidates,
  objecttype,
}: CreateEnrichObjectTypeActionsOptions) {
  const subMenu = createActions(enrichmentCandidates, (enrichment) => createEnrichmentAction(objecttype, enrichment));
  return { label: i18n('objecttype.action.enrichment'), subMenu };
}

export function createEnrichAttributeActions({ enrichmentCandidates, attribute }: CreateEnrichAttributeActionsOptions) {
  if (!enrichmentActionsAvailable()) {
    return [];
  }
  const filteredEnrichments = enrichmentCandidates.filter((enrichment) =>
    isAttributeIdToBeEnrichedAvailable(attribute.id, enrichment.attributeIds),
  );
  const subMenu = createActions(filteredEnrichments, (enrichmentCandidate) => {
    const enrichment = getEnrichmentByAttributeId(getStore().getState(), attribute.id as AttributeId);
    const objecttype = getObjecttypeById(
      getStore().getState(),
      enrichment?.enrichedObjecttypeId || attribute.objecttypeId,
    );
    return createEnrichmentAction(objecttype, enrichmentCandidate, attribute);
  });
  return [{ label: i18n('attribute.action.enrichment'), subMenu }];
}

function createActions(
  enrichments: EnrichmentCandidateDto[],
  factory: (enrichment: EnrichmentCandidateDto) => MenuItem,
) {
  const enrichmentsWithSeparators: (EnrichmentCandidateDto | MenuItemSeparator)[] = enrichments.sort(compare);
  const insertSeparatorBefore: EnrichmentType[] = [
    'MeasuresFromSpatialIntersectionEnrichment',
    'AnalyticsExtensionEnrichment',
  ];
  for (const type of insertSeparatorBefore) {
    const index = enrichmentsWithSeparators.findIndex(
      (enrichment) => (enrichment as EnrichmentCandidateDto).type === type,
    );
    if (index !== -1) {
      enrichmentsWithSeparators.splice(index, 0, '---');
    }
  }
  return enrichmentsWithSeparators.map((enrichmentOrSeparator) =>
    enrichmentOrSeparator !== '---' ? factory(enrichmentOrSeparator) : '---',
  );
}

function compare(a: EnrichmentCandidateDto, b: EnrichmentCandidateDto) {
  return compareEnrichmentType(a.type, b.type) || compareWithLocale(getPrintName(a), getPrintName(b));
}

function isAttributeIdToBeEnrichedAvailable(
  attributeIdToBeEnriched: AttributeId | ActiveAttributeId,
  attributeIds?: AttributeId[],
) {
  return attributeIds?.find((attributeId) => attributeId === attributeIdToBeEnriched) != null;
}

function createEnrichmentAction(
  objecttype: Objecttype,
  enrichmentCandidate: EnrichmentCandidateDto,
  attribute?: WorkbookAttributeDto,
): MenuItem {
  return {
    label: getPrintName(enrichmentCandidate),
    disabled: false,
    execute: () => createEnrichment(objecttype, enrichmentCandidate, attribute),
  };
}

function getPrintName(enrichmentCandidate: EnrichmentCandidateDto) {
  if ('printName' in enrichmentCandidate) {
    return enrichmentCandidate.printName as string;
  }
  return i18n(`enrichment.action.<${enrichmentCandidate.type}>.printName`);
}

export function observeForEnrichmentResult(workOrderId: WorkOrderId) {
  return observeForResult<never, WorkOrderProgressEvent, EnrichmentResultEvent>(
    workOrderId,
    'enrichment-object-type-created',
  );
}
